import { useEffect, useRef, useState, type InputHTMLAttributes, type ForwardRefExoticComponent, type KeyboardEvent, type Ref, type RefAttributes } from 'react';
import PhoneInput, { getCountries, getCountryCallingCode, isSupportedCountry, type Country } from 'react-phone-number-input/max';
import type { Props as InternationalPhoneInputProps } from 'react-phone-number-input';
import labels from 'react-phone-number-input/locale/en.json';
import flags from 'react-phone-number-input/flags';
import { captureException } from '../../lib/telemetry/posthog/frontend';
import 'react-phone-number-input/style.css';

/** International telephone control fitted to the existing text input surfaces. */
interface PhoneNumberInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit?: () => void;
  disabled?: boolean;
  placeholder?: string;
  labelledBy?: string;
  inputLabel?: string;
  describedBy?: string;
  invalid?: boolean;
  appearance?: 'standalone' | 'composer' | 'preview';
  inputClassName?: string;
  inputRef?: Ref<HTMLInputElement>;
}

// The library forwards its ref to the native input, but its declaration still describes the old class component.
const InternationalPhoneInput = PhoneInput as unknown as ForwardRefExoticComponent<
  InternationalPhoneInputProps<InputHTMLAttributes<HTMLInputElement>> & RefAttributes<HTMLInputElement>
>;

const countryLabels = { ...labels, ...Object.fromEntries(getCountries().map(country => [country,
  `${labels[country]} (+${getCountryCallingCode(country)})`])) };

/** Preselect coarse location once, while preserving existing numbers and any respondent edits. */
export function PhoneNumberInput({ value, onChange, onSubmit, disabled, placeholder, labelledBy, inputLabel, describedBy, invalid,
  appearance = 'standalone', inputClassName, inputRef }: PhoneNumberInputProps) {
  const [country, setCountry] = useState<Country>();
  const interacted = useRef(Boolean(value));
  useEffect(() => {
    if (disabled || interacted.current) return undefined;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);
    void (async () => {
      try {
        const response = await fetch('/api/location', { signal: controller.signal, cache: 'no-store' });
        if (!response.ok) throw new Error('Country lookup unavailable');
        const result: unknown = await response.json();
        const detected = result && typeof result === 'object' && 'country' in result ? result.country : undefined;
        if (!controller.signal.aborted && !interacted.current && typeof detected === 'string' && isSupportedCountry(detected)) setCountry(detected);
      } catch (error) {
        if (!controller.signal.aborted) captureException(error instanceof Error ? error : new Error('Country lookup failed'), 'PhoneNumberInput.location');
      } finally { clearTimeout(timeout); }
    })();
    return () => { controller.abort(); clearTimeout(timeout); };
  }, [disabled]);

  const frame = appearance === 'standalone'
    ? 'rounded-lg border border-border px-3 focus-within:outline-1 focus-within:-outline-offset-1 focus-within:outline-ring'
    : appearance === 'composer' ? 'pl-2' : '';
  const typography = appearance === 'preview' ? 'p-0 text-sm leading-5 text-muted-foreground'
    : 'py-2.5 text-base leading-6 text-foreground disabled:opacity-60';
  return <InternationalPhoneInput ref={inputRef} international flags={flags} labels={countryLabels} defaultCountry={country}
    value={value || undefined} onChange={next => { interacted.current = true; onChange(next ?? ''); }}
    onCountryChange={() => { interacted.current = true; }}
    disabled={disabled} placeholder={placeholder || 'Phone number'} autoComplete="tel" inputMode="tel"
    aria-label={labelledBy ? undefined : inputLabel ?? 'Phone number'} aria-labelledby={labelledBy}
    aria-describedby={describedBy} aria-invalid={invalid}
    countrySelectProps={{ 'aria-label': 'Country calling code' }}
    numberInputProps={{ className: inputClassName ?? `min-w-0 flex-1 bg-transparent ${typography} outline-none placeholder:text-muted-foreground` }}
    className={`flex min-w-0 flex-1 items-center bg-transparent ${frame} [--PhoneInput-color--focus:var(--color-ring)] [&_.PhoneInputCountrySelect]:bg-surface [&_.PhoneInputCountrySelect]:text-foreground`}
    onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter' && (event.nativeEvent.isComposing || event.keyCode === 229)) event.preventDefault();
      else if (event.key === 'Enter' && !event.shiftKey && onSubmit) { event.preventDefault(); if (!disabled) onSubmit(); }
    }} />;
}
